import React, { useState, useEffect } from 'react';
import {
    InfoCard,
    Progress,
    ResponseErrorPanel,
    OverflowTooltip,
    Link,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
    catalogApiRef,
    useEntity,
    getEntityRelations,
    humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import {
    stringifyEntityRef,
    parseEntityRef,
    RELATION_PARENT_OF,
    RELATION_MEMBER_OF,
    GroupEntity,
} from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';
import qs from 'qs';
import { uniqBy, uniq } from 'lodash';
import limiterFactory from 'p-limit';
import pluralize from 'pluralize';
import {
    Box,
    Grid,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Switch,
    Tooltip,
    makeStyles,
    createStyles,
} from '@material-ui/core';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { createExternalRouteRef } from '@backstage/core-plugin-api';

// ---------------------------------------------------------------------------
// Route ref — same external route the original card uses
// ---------------------------------------------------------------------------
const catalogIndexRouteRef = createExternalRouteRef({
    id: 'catalog-index-custom',
    optional: true,
});
// Bind to the real catalog route via the plugin's route
// We'll use generatePath manually instead to avoid the external ref binding issue
// and just hard-code /catalog since that's where CustomCatalogPage lives.
const CATALOG_PATH = '/catalog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type EntityRelationAggregation = 'direct' | 'aggregated';

interface OwnedEntityCounter {
    counter: number;
    type: string | undefined;
    kind: string;
    queryParams: string;
}

// ---------------------------------------------------------------------------
// Query param builder
// ---------------------------------------------------------------------------
const buildQueryParams = (
    ownersEntityRef: string[],
    selectedEntity: { kind: string; type: string | undefined },
): string => {
    const { kind, type } = selectedEntity;
    const owners = ownersEntityRef.map(owner =>
        humanizeEntityRef(parseEntityRef(owner), { defaultKind: 'group' }),
    );
    const filters: Record<string, unknown> = {
        kind: kind.toLocaleLowerCase('en-US'),
        type: type ? type.toLocaleLowerCase('en-US') : undefined,
        owners: owners.length > 0 ? owners : undefined,
    };
    // Remove undefined, null, and empty-string values so qs doesn't serialize them
    Object.keys(filters).forEach(k => {
        if (filters[k] === undefined || filters[k] === null || filters[k] === '') {
            delete filters[k];
        }
    });
    return qs.stringify({ filters }, { arrayFormat: 'repeat' });
};

// ---------------------------------------------------------------------------
// Owner resolution (copied logic from original useGetEntities)
// ---------------------------------------------------------------------------
const limiter = limiterFactory(5);

const isEntity = (e: unknown): e is object => e !== undefined;

const getChildOwnershipEntityRefs = async (
    entity: any,
    catalogApi: any,
    alreadyRetrieved: string[] = [],
): Promise<string[]> => {
    const childGroups = getEntityRelations(entity, RELATION_PARENT_OF, { kind: 'Group' });
    const entityRef = stringifyEntityRef(entity);
    if (childGroups.length > 0) {
        const entityRefs = childGroups.map(r => stringifyEntityRef(r));
        const response = await limiter(() =>
            catalogApi.getEntitiesByRefs({
                fields: ['kind', 'metadata.namespace', 'metadata.name', 'relations'],
                entityRefs,
            }),
        );
        const children = (response.items as any[]).filter(isEntity);
        const unknown = children.filter(
            c => !alreadyRetrieved.includes(stringifyEntityRef(c)),
        );
        const childRefs = (
            await Promise.all(
                unknown.map((c: any) =>
                    getChildOwnershipEntityRefs(c, catalogApi, [
                        ...alreadyRetrieved,
                        entityRef,
                    ]),
                ),
            )
        ).flat();
        return uniq([...childRefs, entityRef]);
    }
    return [entityRef];
};

const getOwners = async (
    entity: any,
    aggregation: EntityRelationAggregation,
    catalogApi: any,
): Promise<string[]> => {
    if (aggregation === 'aggregated' && entity.kind === 'Group') {
        return getChildOwnershipEntityRefs(entity, catalogApi);
    }
    if (aggregation === 'aggregated' && entity.kind === 'User') {
        const parentGroups = getEntityRelations(entity, RELATION_MEMBER_OF, { kind: 'Group' });
        const groupRefs = parentGroups.map(({ kind, namespace, name }) =>
            stringifyEntityRef({ kind, namespace, name }),
        );
        return [...groupRefs, stringifyEntityRef(entity)];
    }
    return [stringifyEntityRef(entity)];
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const batchGetOwned = async (
    owners: string[],
    kinds: string[],
    catalogApi: any,
): Promise<any[]> => {
    const results: any[] = [];
    const batchSize = 100;
    for (let i = 0; i < owners.length; i += batchSize) {
        const batch = owners.slice(i, i + batchSize);
        const response = await catalogApi.getEntities({
            filter: [{ kind: kinds, 'relations.ownedBy': batch }],
            fields: ['kind', 'metadata.name', 'metadata.namespace', 'spec.type', 'relations'],
        });
        results.push(...response.items);
        if (i + batchSize < owners.length) await delay(100);
    }
    return uniqBy(results, stringifyEntityRef);
};

// ---------------------------------------------------------------------------
// useGetEntities hook — same as original but with custom query params
// ---------------------------------------------------------------------------
function useGetEntities(
    entity: any,
    aggregation: EntityRelationAggregation,
    entityFilterKind: string[] | undefined,
    entityLimit: number,
) {
    const catalogApi = useApi(catalogApiRef);
    const kinds = entityFilterKind ?? ['Component', 'API', 'System'];

    const { loading, error, value: componentsWithCounters } = useAsync(async () => {
        const owners = await getOwners(entity, aggregation, catalogApi);
        const owned = await batchGetOwned(owners, kinds, catalogApi);

        const counts = owned.reduce<{ kind: string; type: string | undefined; count: number }[]>(
            (acc, e) => {
                const match = acc.find(x => x.kind === e.kind && x.type === e.spec?.type);
                if (match) {
                    match.count += 1;
                } else {
                    acc.push({ kind: e.kind, type: e.spec?.type?.toString(), count: 1 });
                }
                return acc;
            },
            [],
        );

        const topN = counts.sort((a, b) => b.count - a.count).slice(0, entityLimit);

        return topN.map(entry => ({
            counter: entry.count,
            type: entry.type,
            kind: entry.kind,
            queryParams: buildQueryParams(owners, entry),
        }));
    }, [catalogApi, entity, aggregation]);

    return { componentsWithCounters, loading, error };
}

// ---------------------------------------------------------------------------
// EntityCountTile
// ---------------------------------------------------------------------------
const useTileStyles = makeStyles(
    theme =>
        createStyles({
            card: {
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[2],
                borderRadius: '4px',
                padding: theme.spacing(2),
                transition: `${theme.transitions.duration.standard}ms`,
                '&:hover': { boxShadow: theme.shadows[4] },
                height: '100%',
            },
            bold: { fontWeight: theme.typography.fontWeightBold },
            smallFont: { fontSize: theme.typography.body2.fontSize },
            entityTypeBox: {
                background: (props: { type: string }) =>
                    (theme as any).getPageTheme?.({ themeId: props.type })?.backgroundImage ?? '',
                color: (props: { type: string }) =>
                    (theme as any).getPageTheme?.({ themeId: props.type })?.fontColor ?? '',
            },
        }),
    { name: 'CustomOwnershipEntityCountTile' },
);

const EntityCountTile = ({
    counter,
    type,
    kind,
    url,
}: {
    counter: number;
    type: string | undefined;
    kind: string;
    url: string | undefined;
}) => {
    const classes = useTileStyles({ type: type ?? kind });
    const rawTitle = type ?? kind;
    const isLongText = rawTitle.length > 10;

    const tile = (
        <Box
            className={`${classes.card} ${classes.entityTypeBox}`}
            display="flex"
            flexDirection="column"
            alignItems="center"
        >
            <Typography className={classes.bold} variant="h6">
                {counter}
            </Typography>
            <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Typography
                    className={`${classes.bold} ${isLongText ? classes.smallFont : ''}`}
                    variant="h6"
                >
                    <OverflowTooltip
                        text={pluralize(rawTitle.toLocaleUpperCase('en-US'), counter)}
                    />
                </Typography>
            </Box>
            {type && <Typography variant="subtitle1">{kind}</Typography>}
        </Box>
    );

    if (url) {
        return (
            <Link to={url} variant="body2">
                {tile}
            </Link>
        );
    }
    return tile;
};

// ---------------------------------------------------------------------------
// ComponentsGrid
// ---------------------------------------------------------------------------
const useGridStyles = makeStyles(
    () => createStyles({ grid: { overflowY: 'auto', marginTop: 0 } }),
    { name: 'CustomOwnershipComponentsGrid' },
);

const ComponentsGrid = ({
    entity,
    aggregation,
    entityFilterKind,
    entityLimit,
}: {
    entity: any;
    aggregation: EntityRelationAggregation;
    entityFilterKind?: string[];
    entityLimit: number;
}) => {
    const classes = useGridStyles();
    const { componentsWithCounters, loading, error } = useGetEntities(
        entity,
        aggregation,
        entityFilterKind,
        entityLimit,
    );

    if (loading) return <Progress />;
    if (error) return <ResponseErrorPanel error={error} />;
    if (!componentsWithCounters || componentsWithCounters.length === 0) {
        return (
            <Box pt={2} pb={1} px={2}>
                <Typography variant="body2">Nenhuma entidade encontrada</Typography>
            </Box>
        );
    }
    componentsWithCounters.forEach(c => console.log('tile:', { kind: c.kind, type: c.type, queryParams: decodeURIComponent(c.queryParams) }));

    return (
        <Grid container className={classes.grid}>
            {componentsWithCounters?.map(c => (
                <Grid item xs={6} md={6} lg={4} key={c.type ?? c.kind}>
                    <EntityCountTile
                        counter={c.counter}
                        kind={''}
                        type={c.type}
                        url={`${CATALOG_PATH}?${c.queryParams}`}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

// ---------------------------------------------------------------------------
// CustomOwnershipCard — top-level component
// ---------------------------------------------------------------------------
const useCardStyles = makeStyles(
    theme =>
        createStyles({
            card: { maxHeight: '100%' },
            cardContent: { display: 'flex', flexDirection: 'column', overflow: 'hidden' },
            list: {
                [theme.breakpoints.down('xs')]: { padding: '0 0 12px' },
            },
            listItemText: {
                [theme.breakpoints.down('xs')]: { paddingRight: 0, paddingLeft: 0 },
            },
            listItemSecondaryAction: {
                [theme.breakpoints.down('xs')]: {
                    width: '100%',
                    top: 'auto',
                    right: 'auto',
                    position: 'relative',
                    transform: 'unset',
                },
            },
            box: {
                overflowY: 'auto',
                padding: theme.spacing(0, 1, 1),
                margin: theme.spacing(0, -1),
            },
        }),
    { name: 'CustomOwnershipCard' },
);

type Props = {
    variant?: 'gridItem' | 'default' | 'fullHeight';
    entityFilterKind?: string[];
    hideRelationsToggle?: boolean;
    relationAggregation?: EntityRelationAggregation;
    entityLimit?: number;
    maxScrollHeight?: string;
};

export const CustomOwnershipCard = ({
    variant,
    entityFilterKind,
    hideRelationsToggle = false,
    relationAggregation,
    entityLimit = 6,
    maxScrollHeight: propMaxScrollHeight,
}: Props) => {
    const classes = useCardStyles();
    const { entity } = useEntity();

    const defaultAggregation: EntityRelationAggregation =
        entity.kind === 'User' ? 'aggregated' : 'direct';
    const [aggregation, setAggregation] = useState<EntityRelationAggregation>(
        relationAggregation ?? defaultAggregation,
    );

    useEffect(() => {
        if (!relationAggregation) setAggregation(defaultAggregation);
    }, [defaultAggregation, relationAggregation]);

    const maxScrollHeight = variant !== 'fullHeight' ? propMaxScrollHeight : undefined;

    return (
        <InfoCard
            title="Relações"
            variant={variant}
            className={classes.card}
            cardClassName={classes.cardContent}
        >
            {/* {!hideRelationsToggle && (
                <List dense>
                    <ListItem className={classes.list}>
                        <ListItemText className={classes.listItemText} />
                        <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
                            Relações Diretas
                            <Tooltip
                                placement="top"
                                arrow
                                title={
                                    aggregation === 'direct'
                                        ? 'Relações Diretas'
                                        : 'Relações Agregadas'
                                }
                            >
                                <Switch
                                    color="primary"
                                    checked={aggregation !== 'direct'}
                                    onChange={() =>
                                        setAggregation(a =>
                                            a === 'direct' ? 'aggregated' : 'direct',
                                        )
                                    }
                                    name="aggregation"
                                    inputProps={{ 'aria-label': 'Alternar agregação de relações' }}
                                />
                            </Tooltip>
                            Relações Agregadas
                        </ListItemSecondaryAction>
                    </ListItem>
                </List>
            )} */}
            <Box maxHeight={maxScrollHeight} className={classes.box}>
                <ComponentsGrid
                    entity={entity}
                    aggregation={aggregation}
                    entityFilterKind={entityFilterKind}
                    entityLimit={entityLimit}
                />
            </Box>
        </InfoCard>
    );
};
